import { snakeCase } from 'snake-case';
import anyAscii from 'any-ascii';
import { type DataLayerEvent, type GA4EcommerceEvent } from '.';

const search_page_referrer: string | undefined = undefined;

export function eventDecorator(event: GA4EcommerceEvent | DataLayerEvent): GA4EcommerceEvent | DataLayerEvent {
  event.ci_level = 0;
  if (event.event === 'search_result') {
    event.event_info.page_referrer = search_page_referrer;
  }

  if (event.ecommerce) {
    event.ecommerce = objectValuesToSnakeCaseRecursive(event.ecommerce, [
      'sku',
      'item_id',
      'transaction_id',
      'item_list_id',
      'promotion_id',
      'content_id',
      'content_link',
      'cart_discount',
      'cart_discount_key',
      'coupon',
      'page_location',
      'page_path',
      'page_referrer',
      'customer_email',
      'retail_media_ad_id',
      'retail_media_session_id',
      'timestamp',
    ]);
  }
  if (event.event_info) {
    event.event_info = objectValuesToSnakeCaseRecursive(event.event_info, [
      'page_location',
      'page_path',
      'page_referrer',
      'page_internal_referrer',
      'content_id',
      'content_link',
      'language',
      'market',
      'retail_media_session_id',
      'timestamp',
    ]);
  }

  // As the default behaviour of recursively merging objects in the datalayer creates more problems
  // than it solves, we set the _clear flag to true unless explicitly set to false.
  if (!('_clear' in event)) {
    event._clear = true;
  }

  return event;
}

const objectValuesToSnakeCaseRecursive = (obj: any, ignored_keys?: Array<string>) => {
  if (typeof obj !== 'object') {
    return obj;
  }

  const snakeCaseObj: Record<string, any> = {};
  for (const key in obj) {
    if (typeof obj[key] === 'undefined') {
      snakeCaseObj[key] = 'not_available';
      continue;
    }
    if (ignored_keys?.includes(key)) {
      snakeCaseObj[key] = obj[key];
      continue;
    }
    if (obj[key] instanceof Array) {
      snakeCaseObj[key] = obj[key].map((x: any) => objectValuesToSnakeCaseRecursive(x, ignored_keys));
      continue;
    }
    if (typeof obj[key] === 'object') {
      snakeCaseObj[key] = objectValuesToSnakeCaseRecursive(obj[key], ignored_keys);
      continue;
    }
    if (typeof obj[key] === 'string') {
      if (/^\d+(?:\.\d+)?\-\d+(?:\.\d+)?$/.test(obj[key])) {
        snakeCaseObj[key] = obj[key];
        continue;
      }
      if (obj[key].indexOf(', ') > -1 && obj[key][0] === '[') {
        snakeCaseObj[key] = obj[key]
          .replace(/^\[|\]$/g, '')
          .split(', ')
          .map((x: string) => snakeCase(anyAscii(x).replace(/\s/g, '_')))
          .join(', ');
        continue;
      }
      snakeCaseObj[key] = snakeCase(
        anyAscii(obj[key])
          .replace(/^\[|\]$/g, '')
          .replace(/\s/g, '_'),
      );
      continue;
    }
    snakeCaseObj[key] = obj[key];
  }
  return snakeCaseObj;
};
